/**
 * DecisionModal · Chunk 10 · Should-Cost five-panel boardroom view.
 *
 * Renders a single decision_queue row from Chunk 9 with full evidence trace.
 * Every numeric value carries a source citation visible on hover. Tiffany Blue
 * (#0D9488) accent, Alabaster (#FAF9F6) surface, Deep Slate (#0F172A) ink.
 *
 * Five panels:
 *   1. Build-up · per-driver rows with humanly-rendered equation + clickable
 *      source citations.
 *   2. Benchmark cross-check · GSA Schedule (always labelled ceiling), PPI
 *      trend, FPDS median where available.
 *   3. Supplier context · federal status (Chunk 1) + financial pressure
 *      (Chunk 5) + federal revenue (Chunk 2).
 *   4. Likely objections + templated counters.
 *   5. Recommended action + Performance-Ledger action recording.
 */
import React, { useEffect, useState } from "react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "../components/ui/dialog";
import { Popover, PopoverContent, PopoverTrigger } from "../components/ui/popover";
import { api, fmtUSD, fmtPct } from "../lib/api";

const TIFFANY = "#0D9488";
const SLATE_INK = "#0F172A";
const ALABASTER = "#FAF9F6";

const ACTION_LABEL = {
  negotiate:    "Negotiate",
  switch:       "Switch supplier",
  consolidate:  "Consolidate",
  defer:        "Defer · escalate",
  no_action:    "No action",
};
const ACTION_TONE = {
  negotiate:   { bg: "#0D9488", fg: "#FFFFFF" },
  switch:      { bg: "#B45309", fg: "#FFFFFF" },
  consolidate: { bg: "#0F172A", fg: "#F9FAFB" },
  defer:       { bg: "#F3F4F6", fg: "#0F172A" },
  no_action:   { bg: "#E5E7EB", fg: "#374151" },
};

const fmtMoney = (n) => (n == null ? "—" : `$${Number(n).toLocaleString("en-US",
  { minimumFractionDigits: 2, maximumFractionDigits: 4 })}`);

export default function DecisionModal({ open, onOpenChange, decisionId }) {
  const [d, setD] = useState(null);
  const [narr, setNarr] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open || !decisionId) return;
    setLoading(true);
    setNarr(null);
    Promise.all([
      api.decisionDetail(decisionId),
      api.decisionNarrative(decisionId).catch(() => null),
    ])
      .then(([detail, narrative]) => {
        setD(detail);
        setNarr(narrative);
      })
      .finally(() => setLoading(false));
  }, [open, decisionId]);

  if (!open) return null;
  const evidence = (d?.evidence) || [];
  const counters = (d?.objection_counters) || [];

  // Partition evidence into build-up rows vs source rows.
  const buildupRows = evidence.filter((e) => e.source === "computed");
  // Map each computed row to its inputs (evidence rows preceding it that
  // share the same equation expression). v1 simple grouping.
  const inputsForStep = (step) => evidence.filter(
    (e) => e.source !== "computed" && e.used_in_equation === step.used_in_equation
  );

  const gapPct = d?.gap_pct;
  const gapTone =
    gapPct == null ? "neutral" :
    Math.abs(gapPct) > 15 ? "red" :
    Math.abs(gapPct) > 5 ? "amber" : "neutral";
  const gapColor = { red: "#9F1239", amber: "#B45309", neutral: SLATE_INK }[gapTone];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="decision-modal"
        className="max-w-5xl max-h-[92vh] overflow-y-auto"
        style={{ background: ALABASTER, color: SLATE_INK, border: `1px solid ${TIFFANY}33` }}
      >
        <DialogHeader>
          <DialogTitle className="text-2xl tracking-tight" style={{ fontFamily: '"Cormorant Garamond", serif' }}>
            Should-Cost Decision
          </DialogTitle>
        </DialogHeader>

        {loading || !d ? (
          <div className="py-10 text-center text-sm" style={{ color: SLATE_INK }}>
            Loading evidence trail…
          </div>
        ) : (
          <div className="space-y-5">
            {/* ── HEADER STRIP ── */}
            <HeaderStrip d={d} gapColor={gapColor} />

            {/* ── REASONING (boardroom narrator · Bridgewater grade) ── */}
            {narr && (narr.narrative_html || narr.claim_html) && (
              <ReasoningPanel narr={narr} />
            )}

            {/* ── PANEL 1 · BUILD-UP ── */}
            <Panel
              num="01"
              title="Build-up"
              subtitle={
                d.compute_method === "build_up"
                  ? `Template ${d.template_id} v${d.template_version} · match confidence ${(d.match_confidence * 100).toFixed(0)}%`
                  : d.compute_method === "benchmark_only"
                  ? "Benchmark-only · no template match ≥0.7"
                  : "No compute path · see error log"
              }
            >
              {buildupRows.length === 0 ? (
                <div className="text-sm" data-testid="buildup-empty">
                  {d.error_message ? (
                    <span style={{ color: "#9F1239" }}>Build-up halted · {d.error_message}</span>
                  ) : (
                    <span style={{ color: "#64748B" }}>
                      No build-up rendered. {d.compute_method === "benchmark_only" && "Benchmark-only path produces a single cross-check value (Panel 2)."}
                    </span>
                  )}
                </div>
              ) : (
                <div className="space-y-1.5">
                  {buildupRows.map((step, idx) => (
                    <BuildupRow key={idx} step={step} inputs={inputsForStep(step)} />
                  ))}
                </div>
              )}
            </Panel>

            {/* ── PANEL 2 · BENCHMARK CROSS-CHECK ── */}
            <Panel num="02" title="Benchmark cross-check">
              <BenchmarkCrossCheck d={d} />
            </Panel>

            {/* ── PANEL 3 · SUPPLIER CONTEXT ── */}
            <Panel num="03" title="Supplier context">
              <SupplierContext d={d} />
            </Panel>

            {/* ── PANEL 4 · OBJECTIONS + COUNTERS ── */}
            <Panel
              num="04"
              title="Likely objections and counters"
              subtitle="Templated counter text · not LLM-generated"
            >
              {counters.length === 0 ? (
                <div className="text-sm" style={{ color: "#64748B" }}>
                  No objection counters available for this category.
                </div>
              ) : (
                <div className="grid md:grid-cols-3 gap-3">
                  {counters.map((c, i) => (
                    <ObjectionCard key={i} c={c} />
                  ))}
                </div>
              )}
            </Panel>

            {/* ── PANEL 5 · ACTION ── */}
            <Panel num="05" title="Recommended action">
              <ActionPanel d={d} onLogged={() => api.decisionDetail(decisionId).then(setD)} />
            </Panel>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

/* ─────────────────────────── sub-components ─────────────────────────── */

function ReasoningPanel({ narr }) {
  // Render the boardroom-grade reasoning prose on a cream paper card
  // that mirrors the published artifact aesthetic. Same source of truth.
  return (
    <div
      className="rounded-md p-5"
      style={{
        background: "#FAF5EB",
        border: "1px solid #C9BFA8",
        fontFamily: '"EB Garamond", "Cormorant Garamond", Garamond, "Times New Roman", serif',
        color: "#1A1A1A",
        lineHeight: 1.62,
      }}
      data-testid="decision-reasoning-panel"
    >
      <div className="text-[10px] tracking-[0.35em] mb-2" style={{ color: "#0D6964" }}>
        REASONING
      </div>
      {narr.claim_html && (
        <div
          className="text-[15px] italic mb-3"
          style={{ color: "#1A1A1A", maxWidth: "60ch" }}
          dangerouslySetInnerHTML={{ __html: narr.claim_html }}
        />
      )}
      {narr.narrative_html && (
        <div
          className="text-[14px]"
          style={{ color: "#1A1A1A", maxWidth: "62ch" }}
          dangerouslySetInnerHTML={{ __html: narr.narrative_html }}
        />
      )}
      {narr.cost_bar_svg && (
        <div
          className="mt-3"
          dangerouslySetInnerHTML={{ __html: narr.cost_bar_svg }}
        />
      )}
      {narr.company && narr.company !== "the supplier" && (
        <div className="text-[10px] mt-3 italic" style={{ color: "#4A4A4A" }}>
          Supplier of record · {narr.company}
        </div>
      )}
    </div>
  );
}

function HeaderStrip({ d, gapColor }) {
  return (
    <div
      className="grid md:grid-cols-3 gap-4 p-4 rounded-md"
      style={{ background: "#FFFFFF", border: `1px solid ${TIFFANY}22` }}
      data-testid="decision-header-strip"
    >
      <Stat label="Quoted price"      value={fmtMoney(d.actual_price_per_unit)} />
      <Stat label="Supportable price" value={fmtMoney(d.supportable_price_per_unit)}
            band={d.confidence_band_pct ? `±${d.confidence_band_pct}% confidence` : null}
            tone="tiffany" />
      <Stat label="Gap" value={d.gap_pct == null ? "—" : `${d.gap_pct > 0 ? "+" : ""}${d.gap_pct.toFixed(1)}%`}
            sub={d.gap_total_usd == null ? null : `${d.gap_total_usd >= 0 ? "+" : "−"}${fmtUSD(Math.abs(d.gap_total_usd))} total`}
            tone="raw" valueColor={gapColor} />
    </div>
  );
}

function Stat({ label, value, sub, band, tone, valueColor }) {
  return (
    <div>
      <div className="text-[10px] tracking-[0.18em] uppercase" style={{ color: "#64748B" }}>{label}</div>
      <div className="text-2xl mt-1 font-medium" style={{ color: valueColor || (tone === "tiffany" ? TIFFANY : SLATE_INK), fontFamily: '"Cormorant Garamond", serif' }}>
        {value}
      </div>
      {sub && <div className="text-xs mt-0.5" style={{ color: "#475569" }}>{sub}</div>}
      {band && <div className="text-[10px] mt-1" style={{ color: TIFFANY }}>{band}</div>}
    </div>
  );
}

function Panel({ num, title, subtitle, children }) {
  return (
    <div className="rounded-md" style={{ background: "#FFFFFF", border: "1px solid #E2E8F0" }}>
      <div className="px-4 py-2.5 border-b" style={{ borderColor: "#E2E8F0" }}>
        <div className="flex items-baseline gap-3">
          <span className="text-[10px] tracking-[0.18em] uppercase" style={{ color: TIFFANY }}>{num}</span>
          <h3 className="text-base font-medium" style={{ fontFamily: '"Cormorant Garamond", serif', fontSize: "1.05rem" }}>{title}</h3>
        </div>
        {subtitle && <div className="text-xs mt-0.5" style={{ color: "#64748B" }}>{subtitle}</div>}
      </div>
      <div className="p-4">{children}</div>
    </div>
  );
}

function BuildupRow({ step, inputs }) {
  return (
    <div className="grid grid-cols-12 gap-2 py-1.5 border-b last:border-0" style={{ borderColor: "#F1F5F9" }}>
      <div className="col-span-3 text-sm font-medium" style={{ color: SLATE_INK }}>
        {step.variable || "Step"}
      </div>
      <div className="col-span-7 text-xs flex flex-wrap gap-1.5 items-center" style={{ color: "#475569" }}>
        <span>{step.human_readable || step.used_in_equation}</span>
        {inputs.map((inp, i) => (
          <SourcePopover key={i} src={inp} />
        ))}
      </div>
      <div className="col-span-2 text-right text-sm tabular-nums" style={{ color: SLATE_INK }}>
        {step.value == null ? "—" : `$${Number(step.value).toLocaleString("en-US", { maximumFractionDigits: 4 })}`}
      </div>
    </div>
  );
}

function SourcePopover({ src }) {
  const tone = src.source === "po_line_input"
    ? { bg: "#FDF6B2", fg: "#92400E" }
    : src.source === "industry_default"
    ? { bg: "#F1F5F9", fg: "#334155" }
    : { bg: `${TIFFANY}22`, fg: TIFFANY };
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          className="text-[10px] px-1.5 py-0.5 rounded uppercase tracking-wider cursor-pointer hover:opacity-80"
          style={{ background: tone.bg, color: tone.fg }}
          data-testid={`source-pill-${src.source}`}
        >
          {src.variable || src.source} ↗
        </button>
      </PopoverTrigger>
      <PopoverContent className="text-xs max-w-sm" style={{ background: "#FFFFFF", color: SLATE_INK, border: `1px solid ${TIFFANY}44` }}>
        <div className="font-medium mb-1" style={{ color: TIFFANY }}>{src.source}</div>
        <div className="space-y-1">
          <div><span className="text-slate-500">Source ID:</span> <code className="text-[10px]">{src.source_id}</code></div>
          {src.period && <div><span className="text-slate-500">Period:</span> {src.period}</div>}
          <div><span className="text-slate-500">Value:</span> <span className="tabular-nums">{src.value}</span></div>
          {src.human_readable && <div className="pt-1 italic">{src.human_readable}</div>}
          {src.match_kind && src.match_kind !== "exact" && (
            <div className="pt-1 text-[10px]" style={{ color: "#B45309" }}>
              Match: {src.match_kind}
            </div>
          )}
          {src.scaling && (
            <div className="pt-1 text-[10px]" style={{ color: "#64748B" }}>
              Scaled · raw {src.scaling.raw_ppi_index} × {src.scaling.ppi_to_price_factor} = {src.scaling.scaled_value}
            </div>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}

function BenchmarkCrossCheck({ d }) {
  const evidence = d.evidence || [];
  const ppiRow = evidence.find((e) => e.source === "bls_ppi_series");
  const wageRow = evidence.find((e) => e.source === "bls_wage_series");
  const marginRow = evidence.find((e) => e.source === "supplier_financials");
  // GSA Schedule cross-check is conceptual until real GSA CALC data is wired
  const items = [
    ppiRow && {
      label: "BLS PPI · category cost trend",
      value: `Index ${Number(ppiRow.value).toFixed(2)} (${ppiRow.period})`,
      note:  ppiRow.human_readable,
      tone:  "green",
    },
    wageRow && {
      label: "BLS CES · category labor rate",
      value: `$${Number(wageRow.value).toFixed(2)}/hr (${wageRow.period})`,
      note:  wageRow.human_readable,
      tone:  "green",
    },
    marginRow && {
      label: "SEC 10-K · supplier margin disclosure",
      value: `${(Number(marginRow.value) * 100).toFixed(2)}% ${marginRow.margin_type || ""}`,
      note:  marginRow.human_readable,
      tone:  "green",
    },
    {
      label: "GSA Schedule · MFC reference",
      value: "Not wired in v1",
      note:  "Note: GSA Schedule publishes a ceiling rate, NOT what the government actually paid. Task orders typically settle 20–40% below this ceiling.",
      tone:  "amber",
    },
  ].filter(Boolean);

  return (
    <div className="space-y-2">
      {items.map((it, i) => (
        <div key={i} className="grid grid-cols-12 gap-2 py-2 border-b last:border-0" style={{ borderColor: "#F1F5F9" }}>
          <div className="col-span-1 flex items-start pt-1">
            <span className="w-2 h-2 rounded-full"
              style={{ background: it.tone === "green" ? TIFFANY : it.tone === "amber" ? "#B45309" : "#9F1239" }} />
          </div>
          <div className="col-span-5 text-sm" style={{ color: SLATE_INK }}>{it.label}</div>
          <div className="col-span-6 text-xs" style={{ color: "#475569" }}>
            <div className="font-medium" style={{ color: SLATE_INK }}>{it.value}</div>
            <div className="mt-0.5 italic">{it.note}</div>
          </div>
        </div>
      ))}
    </div>
  );
}

function SupplierContext({ d }) {
  const [fin, setFin] = useState(null);
  useEffect(() => {
    if (!d.supplier_cik) return;
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/benchmarks/sec/financials?limit=500`)
      .then((r) => r.json())
      .then((rows) => setFin((rows || []).find((row) => row.cik === d.supplier_cik) || null))
      .catch(() => {});
  }, [d.supplier_cik]);

  return (
    <div className="grid md:grid-cols-3 gap-4">
      <ContextCard title="Federal status (SAM.gov · Chunk 1)">
        {d.supplier_uei ? (
          <div className="text-xs space-y-1" style={{ color: "#475569" }}>
            <div>UEI · <code>{d.supplier_uei}</code></div>
            <div className="text-[10px]" style={{ color: TIFFANY }}>Active federal contractor</div>
          </div>
        ) : (
          <div className="text-xs italic" style={{ color: "#64748B" }}>
            No UEI on file for this supplier. Not a known federal contractor at compute time.
          </div>
        )}
      </ContextCard>

      <ContextCard title="Financial pressure (SEC EDGAR · Chunk 5)">
        {fin ? (
          <div className="text-xs space-y-1" style={{ color: "#475569" }}>
            <div className="font-medium" style={{ color: SLATE_INK }}>{fin.legal_company_name}</div>
            <div>FY{fin.fiscal_year} · revenue {fmtUSD(fin.revenue)}</div>
            <div>Gross margin <span className="tabular-nums">{fin.gross_margin_pct?.toFixed(2)}%</span></div>
            <div>Operating margin <span className="tabular-nums">{fin.operating_margin_pct?.toFixed(2)}%</span></div>
            {fin.last_10k_accession && (
              <div className="text-[10px] pt-1">10-K: <code>{fin.last_10k_accession}</code></div>
            )}
          </div>
        ) : (
          <div className="text-xs italic" style={{ color: "#64748B" }}>
            <span className="inline-block text-[10px] px-1.5 py-0.5 rounded mr-1" style={{ background: "#F1F5F9", color: "#334155" }}>industry-estimated</span>
            Private supplier or no SEC filing on file. Build-up uses industry-default margin from the template (see Panel 1).
          </div>
        )}
      </ContextCard>

      <ContextCard title="Federal revenue (USAspending · Chunk 2)">
        <div className="text-xs italic" style={{ color: "#64748B" }}>
          Per-supplier federal revenue is pulled on demand from USAspending. Not yet auto-joined in the modal in v1.
        </div>
      </ContextCard>
    </div>
  );
}

function ContextCard({ title, children }) {
  return (
    <div className="p-3 rounded-md" style={{ background: "#F8FAFC", border: "1px solid #E2E8F0" }}>
      <div className="text-[10px] tracking-[0.18em] uppercase mb-2" style={{ color: TIFFANY }}>{title}</div>
      {children}
    </div>
  );
}

function ObjectionCard({ c }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="p-3 rounded-md cursor-pointer transition" style={{ background: "#F8FAFC", border: "1px solid #E2E8F0" }}
         onClick={() => setOpen((v) => !v)}>
      <div className="text-[10px] uppercase tracking-wider mb-1" style={{ color: TIFFANY }}>
        {c.objection_code.replace(/_/g, " ")}
      </div>
      <div className="text-xs" style={{ color: SLATE_INK }}>
        {open ? c.counter_text : `${c.counter_text.slice(0, 110)}${c.counter_text.length > 110 ? "…" : ""}`}
      </div>
      <div className="text-[10px] mt-2" style={{ color: "#94A3B8" }}>
        confidence {(c.confidence * 100).toFixed(0)}% · click to {open ? "collapse" : "expand"}
      </div>
    </div>
  );
}

function SendKitForm({ decisionId, onSent }) {
  const [to, setTo] = useState("");
  const [cc, setCc] = useState("");
  const [recipientName, setRecipientName] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(null);
  const [err, setErr] = useState(null);

  const valid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(to.trim());

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setErr(null);
    try {
      const res = await api.sendKit(decisionId, {
        to: to.trim(),
        cc: cc.trim() || null,
        recipient_name: recipientName.trim() || null,
        note: note.trim() || null,
      });
      setSent(res);
      setTimeout(() => onSent && onSent(), 1500);
    } catch (e2) {
      setErr(e2?.response?.data?.detail || e2?.message || "Send failed.");
    } finally {
      setBusy(false);
    }
  };

  if (sent) {
    return (
      <div className="p-3 rounded-md text-xs" style={{ background: `${TIFFANY}11`, border: `1px solid ${TIFFANY}` }}>
        <div className="font-medium" style={{ color: TIFFANY }}>
          ✓ Kit sent to {sent.to}{sent.cc ? ` (cc ${sent.cc})` : ""}
        </div>
        <div className="mt-1" style={{ color: "#475569" }}>
          Resend ID <code>{sent.resend_id?.slice(0, 8)}</code> · audited as send <code>{sent.send_id?.slice(0, 8)}</code>
        </div>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="p-3 rounded-md space-y-2" style={{ background: "#F8FAFC", border: "1px solid #E2E8F0" }} data-testid="send-kit-form">
      <div className="grid grid-cols-2 gap-2">
        <input
          type="email"
          placeholder="recipient@company.com"
          value={to}
          onChange={(e) => setTo(e.target.value)}
          required
          data-testid="send-kit-to"
          className="text-xs px-2 py-1.5 rounded border"
          style={{ borderColor: "#CBD5E1", background: "#FFFFFF", color: SLATE_INK }}
        />
        <input
          type="text"
          placeholder="Recipient name (optional)"
          value={recipientName}
          onChange={(e) => setRecipientName(e.target.value)}
          data-testid="send-kit-name"
          className="text-xs px-2 py-1.5 rounded border"
          style={{ borderColor: "#CBD5E1", background: "#FFFFFF", color: SLATE_INK }}
        />
      </div>
      <input
        type="email"
        placeholder="CC (optional)"
        value={cc}
        onChange={(e) => setCc(e.target.value)}
        data-testid="send-kit-cc"
        className="w-full text-xs px-2 py-1.5 rounded border"
        style={{ borderColor: "#CBD5E1", background: "#FFFFFF", color: SLATE_INK }}
      />
      <textarea
        rows={3}
        placeholder="Short note for the recipient (optional) — included in the email body above the auto-generated briefing summary."
        value={note}
        onChange={(e) => setNote(e.target.value)}
        maxLength={600}
        data-testid="send-kit-note"
        className="w-full text-xs px-2 py-1.5 rounded border"
        style={{ borderColor: "#CBD5E1", background: "#FFFFFF", color: SLATE_INK }}
      />
      <div className="flex items-center justify-between">
        <span className="text-[10px]" style={{ color: "#94A3B8" }}>{note.length}/600 chars</span>
        <button
          type="submit"
          disabled={!valid || busy}
          data-testid="send-kit-submit"
          className="text-xs px-3 py-1.5 rounded font-medium transition"
          style={{
            background: valid && !busy ? TIFFANY : "#CBD5E1",
            color: "#FFFFFF",
            cursor: valid && !busy ? "pointer" : "not-allowed",
          }}
        >
          {busy ? "Sending…" : "Send via email"}
        </button>
      </div>
      {err && (
        <div className="text-[11px] italic" style={{ color: "#9F1239" }} data-testid="send-kit-error">
          {err}
        </div>
      )}
    </form>
  );
}

function ActionPanel({ d, onLogged }) {
  const action = d.recommended_action;
  const tone = ACTION_TONE[action] || ACTION_TONE.defer;
  const [logged, setLogged] = useState(false);
  const [showSend, setShowSend] = useState(false);

  const logAction = async (choice) => {
    // v1 just toasts — Performance Ledger write-back is Wave 3 (Chunk 29)
    setLogged(choice);
    if (onLogged) onLogged();
  };

  const kitUrl = `${process.env.REACT_APP_BACKEND_URL}/api/should-cost/decisions/${d.decision_id}/negotiation-kit.pdf`;

  return (
    <div>
      <div className="flex items-center gap-3 mb-3">
        <span className="text-[10px] uppercase tracking-wider" style={{ color: "#64748B" }}>System recommendation</span>
        <span className="text-xs px-2 py-0.5 rounded font-medium" style={{ background: tone.bg, color: tone.fg }}>
          {ACTION_LABEL[action] || action}
        </span>
      </div>
      <div className="text-xs mb-4" style={{ color: "#475569" }}>
        {actionRationale(d)}
      </div>

      <div className="flex flex-wrap gap-2">
        {["negotiate", "switch", "consolidate", "defer", "no_action"].map((opt) => (
          <button
            key={opt}
            onClick={() => logAction(opt)}
            data-testid={`action-${opt}`}
            className="text-xs px-3 py-1.5 rounded font-medium transition"
            style={{
              background: logged === opt ? TIFFANY : (action === opt ? `${TIFFANY}22` : "#F1F5F9"),
              color: logged === opt ? "#FFFFFF" : SLATE_INK,
              border: action === opt ? `1px solid ${TIFFANY}` : "1px solid transparent",
            }}
          >
            {logged === opt ? "✓ " : ""}I will {ACTION_LABEL[opt].toLowerCase()}
          </button>
        ))}
      </div>

      {/* Negotiation Kit · single-page PDF for the category manager */}
      <div className="mt-4 pt-4 border-t" style={{ borderColor: "#E2E8F0" }}>
        <div className="text-[10px] uppercase tracking-wider mb-2" style={{ color: TIFFANY }}>
          Negotiation kit · single page
        </div>
        <div className="flex flex-wrap gap-2 mb-3">
          <a
            href={kitUrl}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="download-negotiation-kit"
            className="inline-flex items-center gap-2 text-xs px-4 py-2 rounded font-medium transition hover:opacity-90"
            style={{
              background: SLATE_INK, color: "#FAF5EB",
              border: "1px solid " + SLATE_INK, letterSpacing: "0.05em",
            }}
          >
            ↓ Download PDF
          </a>
          <button
            type="button"
            data-testid="open-send-kit"
            onClick={() => setShowSend((v) => !v)}
            className="inline-flex items-center gap-2 text-xs px-4 py-2 rounded font-medium transition hover:opacity-90"
            style={{
              background: showSend ? `${TIFFANY}22` : "#FAF5EB",
              color: SLATE_INK,
              border: `1px solid ${TIFFANY}`,
              letterSpacing: "0.05em",
            }}
          >
            {showSend ? "× Cancel send" : "→ Email kit to category manager"}
          </button>
        </div>
        {showSend && (
          <SendKitForm
            decisionId={d.decision_id}
            onSent={() => { setShowSend(false); }}
          />
        )}
        <div className="text-[10px] mt-2 italic" style={{ color: "#64748B" }}>
          Reasoning chain, three templated counters, and verifiable source IDs — ready to forward before a supplier call.
        </div>
      </div>

      {logged && (
        <div className="mt-3 text-[10px]" style={{ color: TIFFANY }}>
          Action logged · Performance Ledger writeback is Wave 3 (Chunk 29). Decision ID preserved: <code>{d.decision_id}</code>
        </div>
      )}
    </div>
  );
}

function actionRationale(d) {
  if (d.recommended_action === "switch") {
    return `Gap of ${fmtPct(d.gap_pct, 1)} exceeds the 30% threshold for switch consideration. Build-up confidence is ${(d.match_confidence * 100).toFixed(0)}%.`;
  }
  if (d.recommended_action === "negotiate") {
    return `Gap of ${fmtPct(d.gap_pct, 1)} is in the negotiate band (20–30%). Use the three objection counters above as your negotiation kit.`;
  }
  if (d.recommended_action === "no_action") {
    return "Quoted price is within 5% of the supportable price. No material action recommended.";
  }
  if (d.recommended_action === "defer") {
    return d.compute_method === "build_up"
      ? "Gap is in the defer band. Recommend escalating for human review before any negotiation move."
      : "Compute path did not produce a defensible supportable price. Escalate for manual analysis.";
  }
  return "—";
}
